/**
 * Unified Acquisition Attribution — Last Click Wins
 * ==================================================
 * Canonical de-duplication module for all acquisition partners:
 *   - Refgrow        (cookie `refgrow_ref_code` set by latest.js)
 *
 * AuthorityLayer treats every publisher, creator, affiliate, and referral
 * partner as an acquisition partner. Exactly ONE winner per customer.
 *
 * Stores a single canonical record (network + click_id + captured_at)
 * in localStorage + first-party cookie (90 days).
 *
 * Rule: every new acquisition click overwrites the previous one.
 *  - A change in the `refgrow_ref_code` cookie value vs the last-seen
 *    witness counts as a fresh refgrow click.
 *
 * Downstream consumers:
 *   - checkout body → create-checkout edge function → Stripe metadata
 *   - purchase dataLayer event → GTM triggers gate by attribution_network
 *   - Refgrow signup tracking fires ONLY when attribution_network === "refgrow"
 */

export type AttributionNetwork = "refgrow";

export interface AffiliateAttribution {
  attribution_network: AttributionNetwork;
  attribution_click_id: string;
  captured_at: string; // ISO timestamp
}

const STORAGE_KEY = "al_affiliate_attribution";
const COOKIE_NAME = "al_affiliate_attribution";
const REFGROW_COOKIE = "refgrow_ref_code";
const REFGROW_WITNESS_KEY = "al_refgrow_witness";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

const CLICK_ID_RE = /^[A-Za-z0-9_-]{6,256}$/;
// Refgrow ref codes can be shorter / contain different chars. Keep permissive
// but bounded to avoid junk values.
const REFGROW_REF_RE = /^[A-Za-z0-9._~-]{3,256}$/;

const log = (step: string, details?: unknown) => {
  // eslint-disable-next-line no-console
  console.log(`[ATTRIBUTION] ${step}`, details ?? "");
};

function isValidRefgrowCode(v: string | null | undefined): v is string {
  return !!v && REFGROW_REF_RE.test(v);
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const row = document.cookie.split("; ").find((r) => r.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.split("=")[1] ?? "") : null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function clearCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeRemove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

function serialize(attr: AffiliateAttribution): string {
  return JSON.stringify(attr);
}
function parse(raw: string | null): AffiliateAttribution | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    if (!obj) return null;
    const net = obj.attribution_network;
    const okNet = net === "refgrow";
    if (!okNet) return null;
    const id = obj.attribution_click_id;
    const okId =
      typeof id === "string" &&
      (net === "refgrow" ? REFGROW_REF_RE.test(id) : CLICK_ID_RE.test(id));
    if (!okId) return null;
    return {
      attribution_network: net,
      attribution_click_id: id,
      captured_at: typeof obj.captured_at === "string" ? obj.captured_at : new Date().toISOString(),
    };
  } catch { /* ignore */ }
  return null;
}

/** Returns the persisted attribution (localStorage preferred, cookie fallback). */
export function getStoredAttribution(): AffiliateAttribution | null {
  const fromStorage = parse(safeGet(STORAGE_KEY));
  if (fromStorage) return fromStorage;
  const fromCookie = parse(readCookie(COOKIE_NAME));
  if (fromCookie) {
    safeSet(STORAGE_KEY, serialize(fromCookie));
    return fromCookie;
  }
  return null;
}

export function clearStoredAttribution(): void {
  safeRemove(STORAGE_KEY);
  clearCookie(COOKIE_NAME);
}

function persist(attr: AffiliateAttribution): void {
  const raw = serialize(attr);
  safeSet(STORAGE_KEY, raw);
  writeCookie(COOKIE_NAME, raw);
}

function readRefgrowCookie(): string | null {
  const v = readCookie(REFGROW_COOKIE);
  return isValidRefgrowCode(v) ? v : null;
}

/**
 * Inspect the current URL + Refgrow cookie for fresh acquisition clicks
 * and, if found, overwrite the stored attribution (last-click wins).
 *
 * Safe to call on every app boot and on every checkout invocation.
 */
export function captureAffiliateAttribution(): AffiliateAttribution | null {
  if (typeof window === "undefined") return getStoredAttribution();

  try {
    type Candidate = {
      network: AttributionNetwork;
      click_id: string;
      rank: number; // higher = wins
    };
    const candidates: Candidate[] = [];

    // Refgrow: a "fresh click" is detected when the cookie value differs
    // from our last-seen witness. The cookie itself is set by Refgrow's
    // latest.js after they redirect through their domain.
    const refgrowVal = readRefgrowCookie();
    const refgrowWitness = safeGet(REFGROW_WITNESS_KEY);
    const refgrowIsFresh = !!refgrowVal && refgrowVal !== refgrowWitness;
    if (refgrowVal) {
      safeSet(REFGROW_WITNESS_KEY, refgrowVal);
    }
    if (refgrowIsFresh && refgrowVal) {
      // URL params represent an explicit deep-link landing on THIS load and
      // outrank a cookie-witness change. Rank refgrow below URL params but
      // above the empty case.
      candidates.push({ network: "refgrow", click_id: refgrowVal, rank: 500 });
    }

    if (candidates.length === 0) {
      // No fresh click on this load. Seed canonical from refgrow cookie if
      // we have nothing stored at all — covers the "Refgrow only, returning
      // visitor, no URL params" case.
      const existing = getStoredAttribution();
      if (!existing && refgrowVal) {
        const seeded: AffiliateAttribution = {
          attribution_network: "refgrow",
          attribution_click_id: refgrowVal,
          captured_at: new Date().toISOString(),
        };
        persist(seeded);
        log("seeded from refgrow cookie (no prior attribution)", seeded);
        return seeded;
      }
      return existing;
    }

    // Highest rank wins.
    candidates.sort((a, b) => b.rank - a.rank);
    const winner = candidates[0];

    const next: AffiliateAttribution = {
      attribution_network: winner.network,
      attribution_click_id: winner.click_id,
      captured_at: new Date().toISOString(),
    };

    persist(next);
    log("captured acquisition click (last-click wins)", next);
    return next;
  } catch (err) {
    log("capture failed (non-fatal)", { err: String(err) });
    return getStoredAttribution();
  }
}

/**
 * Build the compatibility fields for downstream consumers:
 *   - `refCode` present ONLY when winner === "refgrow"
 *
 * `attribution_network` falls back to "direct" when no acquisition click is stored.
 *
 * Re-runs capture defensively so a Refgrow cookie that landed AFTER the
 * initial main.tsx boot still becomes the canonical winner when applicable.
 */
export function getAttributionForCheckout(): {
  attribution_network: AttributionNetwork | "direct";
  attribution_click_id: string | null;
  refCode: string | null;
} {
  // Defensive re-capture at the last moment before checkout.
  captureAffiliateAttribution();
  const attr = getStoredAttribution();
  if (!attr) {
    return {
      attribution_network: "direct",
      attribution_click_id: null,
      refCode: null,
    };
  }
  return {
    attribution_network: attr.attribution_network,
    attribution_click_id: attr.attribution_click_id,
    refCode: attr.attribution_network === "refgrow" ? attr.attribution_click_id : null,
  };
}

/* ===========================================================================
 * FIRST-PARTY CONTENT CREATOR REFERRALS (`?ref=<code>`)
 * ===========================================================================
 *
 * A separate acquisition TYPE inside this canonical module — it deliberately
 * does not touch the Refgrow record above, so existing acquisition
 * attribution invariants are preserved.
 *
 * Rules (deterministic):
 *   • Any public URL may carry `?ref=<code>`; other query params are untouched.
 *   • Attribution is valid for 90 days FROM the referral event. Navigating the
 *     site or returning directly never resets that clock.
 *   • LAST VALID creator referral wins: a new ACTIVE creator replaces the
 *     previous one and starts a fresh 90-day window.
 *   • An invalid / unknown / malformed / disabled referral NEVER destroys an
 *     existing valid attribution.
 *   • The browser record is convenience only. The server re-resolves the code
 *     at checkout and is the sole financial authority.
 */

export interface CreatorReferral {
  ref_code: string;
  captured_at: string; // ISO timestamp of the referral event
}

export const CREATOR_ATTRIBUTION_WINDOW_DAYS = 90;

const CREATOR_STORAGE_KEY = "al_creator_referral";
const CREATOR_COOKIE_NAME = "al_creator_referral";
const CREATOR_REF_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;

/** Mirrors the server normalizer in `_shared/affiliate-attribution.ts`. */
export function normalizeCreatorRefCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const code = value.trim().toLowerCase();
  if (!code || code.length > 64) return null;
  return CREATOR_REF_RE.test(code) ? code : null;
}

function parseCreatorReferral(raw: string | null): CreatorReferral | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    const code = normalizeCreatorRefCode(obj?.ref_code);
    if (!code) return null;
    const capturedAt = typeof obj?.captured_at === "string" ? obj.captured_at : null;
    if (!capturedAt || Number.isNaN(new Date(capturedAt).getTime())) return null;
    return { ref_code: code, captured_at: capturedAt };
  } catch {
    return null;
  }
}

export function isCreatorReferralWithinWindow(
  referral: CreatorReferral | null,
  now: Date = new Date(),
): boolean {
  if (!referral) return false;
  const at = new Date(referral.captured_at).getTime();
  if (Number.isNaN(at)) return false;
  const ageMs = now.getTime() - at;
  return ageMs >= 0 && ageMs <= CREATOR_ATTRIBUTION_WINDOW_DAYS * 86_400_000;
}

/** Stored creator referral (localStorage preferred, first-party cookie fallback). */
export function getStoredCreatorReferral(): CreatorReferral | null {
  const fromStorage = parseCreatorReferral(safeGet(CREATOR_STORAGE_KEY));
  if (fromStorage) return fromStorage;
  const fromCookie = parseCreatorReferral(readCookie(CREATOR_COOKIE_NAME));
  if (fromCookie) {
    safeSet(CREATOR_STORAGE_KEY, JSON.stringify(fromCookie));
    return fromCookie;
  }
  return null;
}

export function clearStoredCreatorReferral(): void {
  safeRemove(CREATOR_STORAGE_KEY);
  clearCookie(CREATOR_COOKIE_NAME);
}

function persistCreatorReferral(referral: CreatorReferral): void {
  const raw = JSON.stringify(referral);
  safeSet(CREATOR_STORAGE_KEY, raw);
  writeCookie(CREATOR_COOKIE_NAME, raw);
}

/**
 * PURE decision: what should be stored after seeing `incomingCode`?
 * `incomingIsActive` is the server's answer for that code (null = unresolved).
 * Returns the record to persist, or null to leave storage untouched.
 */
export function decideCreatorReferral(args: {
  existing: CreatorReferral | null;
  incomingCode: unknown;
  incomingIsActive: boolean | null;
  now?: Date;
}): CreatorReferral | null {
  const now = args.now ?? new Date();
  const code = normalizeCreatorRefCode(args.incomingCode);
  // Malformed, unknown or disabled referral: never overwrite, never erase.
  if (!code || args.incomingIsActive !== true) return null;
  // Same creator already attributed and still inside their window: do not
  // silently restart the clock.
  if (
    args.existing &&
    args.existing.ref_code === code &&
    isCreatorReferralWithinWindow(args.existing, now)
  ) {
    return null;
  }
  return { ref_code: code, captured_at: now.toISOString() };
}

/** Reads `?ref=` from the current URL without disturbing other parameters. */
export function readCreatorRefFromUrl(search?: string): string | null {
  const raw = search ?? (typeof window !== "undefined" ? window.location.search : "");
  try {
    return normalizeCreatorRefCode(new URLSearchParams(raw ?? "").get("ref"));
  } catch {
    return null;
  }
}

/**
 * Capture a `?ref=` arrival. Validation is delegated to the public
 * `resolve-affiliate-ref` function so an invalid code can never replace a
 * valid stored attribution. Non-blocking and failure-tolerant.
 */
export async function captureCreatorReferral(
  resolver?: (code: string) => Promise<boolean | null>,
): Promise<CreatorReferral | null> {
  if (typeof window === "undefined") return getStoredCreatorReferral();
  const code = readCreatorRefFromUrl();
  const existing = getStoredCreatorReferral();
  if (!code) return existing;

  let isActive: boolean | null = null;
  try {
    isActive = resolver
      ? await resolver(code)
      : await (async () => {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data } = await supabase.functions.invoke("resolve-affiliate-ref", {
          body: { ref: code },
        });
        return (data as { active?: boolean } | null)?.active === true;
      })();
  } catch {
    isActive = null;
  }

  const next = decideCreatorReferral({ existing, incomingCode: code, incomingIsActive: isActive });
  if (!next) return existing;
  persistCreatorReferral(next);
  log("captured creator referral (last valid referral wins)", next);
  return next;
}

/**
 * Creator referral to forward to `create-purchase-intent`, or null when none is
 * stored or the 90-day window has elapsed. The server validates it again.
 */
export function getCreatorReferralForCheckout(now: Date = new Date()): CreatorReferral | null {
  const stored = getStoredCreatorReferral();
  return isCreatorReferralWithinWindow(stored, now) ? stored : null;
}
